import React, { useEffect, useState } from 'react'
// components
import { Tags } from '../../Tags'
// constants
import { Text } from '../../../../constants/TextConstants'
// hooks
import { useInputValue } from '../../../../hooks/useInputValue'
// library of components - Ant Design
import { Col, Modal, Row, Select, Tooltip } from 'antd'
// styles
import { EmptyDiv, ErrorText, Title } from '../../Inputs/styles'
import { LanguageSelectorContainer, LanguageSelectorWrapper } from '../styles'
// utils
import PropTypes from 'prop-types'
import { getLanguageCode, removeTagByIndex, tagsMapper, validateFields } from '../../../../utils/utils'
import { AddNotFound } from '../../AddNotFound'
import { languageLevelsTooltip } from '../../../../constants/languageTooltip'
import { LanguagesSkills } from '../../../../constants/constants'
import { epagAPI } from '../../../../API/epagAPI'
import { useGetToken } from '../../../../hooks/useGetToken'

export const ModalLanguages = ({ initialState = [], isSavedChange, open, setModal, setModelProfile, skillsEnum, threshold = 0 }) => {
  const languageName = useInputValue('')
  const languageLevel = useInputValue('')
  const [languages, setLanguages] = useState([])
  const [languagesList, setLanguagesList] = useState([])
  const [languageLevels, setLanguageLevels] = useState([])
  const [levelIsDisabled, setLevelIsDisabled] = useState(true)

  const langs = 'languages'
  const langLvls = 'language_levels'
  const { token } = useGetToken()
  const [isLoadingData, setIsLoadingData] = useState(true)
  const [ErrorFetch, setErrorFetch] = useState('')
  // Update inner languages property from model incoming update/reload
  useEffect(() => {
    setLanguages(initialState)
  }, [initialState])

  useEffect(() => {
    if (token) {
      Promise.all([getLanguageList(), getLanguageLevels()])
        .then(([languagesResult, levelsResult]) => {
          if (languagesResult && levelsResult) {
            const languages = languagesResult[0].values
            const listCodes = languages.map((item) => {
              return {
                name: item,
                code: getLanguageCode(item)
              }
            })
            setLanguagesList(listCodes)
            setLanguageLevels(levelsResult[0].values)
          } else {
            setErrorFetch('error')
          }
        })
        .catch((error) => {
          setErrorFetch(error.message)
        })
        .finally(() => {
          setIsLoadingData(false)
        })
    }
  }, [token])

  const getLanguageList = () => {
    return epagAPI.GetSkills(langs, token)
  }

  const getLanguageLevels = () => {
    return epagAPI.GetSkills(langLvls, token)
  }

  const closeModal = () => {
    setModal(false)
    isSavedChange(true)
  }

  const handleChange = ({ value, name }) => {
    if (value && name === 'language_name') {
      languageName.setValue(value)
      setLevelIsDisabled(false)
      return
    }
    const languageAdded = {
      language_name: languageName.value,
      level: value
    }
    if (languageAdded.language_name !== '' && languageAdded.level !== '') {
      setLanguages((prevState) => [...prevState, languageAdded])
      clearInputs()
      isSavedChange(false)
    }
  }

  const onSave = () => {
    if (languageName.value && validateFields([languageLevel])) {
      return
    }
    setModelProfile((prevState) => ({ ...prevState, languages: languages }))
    setModal(false)
    isSavedChange(true)
  }

  const clearInputs = () => {
    languageName.setValue('')
    languageName.setError(false)
    languageLevel.setValue('')
    languageLevel.setError(false)
    setLevelIsDisabled(true)
  }

  const removeLanguage = (index) => removeTagByIndex(index, setLanguages)

  const onDragTags = (tags) => {
    const transformedTags = tags.map(({ name, level }) => ({
      language_name: name,
      level
    }))
    setLanguages(transformedTags)
  }

  const { Option } = Select
  return (
    <Modal
      title={Text.Languages}
      open={open}
      maskClosable={false}
      okText={Text.Add}
      onCancel={closeModal}
      onOk={onSave}
      afterClose={clearInputs}
      keyboard={false}>
      {ErrorFetch ? <ErrorText>{Text.ErrorFetch}</ErrorText> : null}
      {isLoadingData ? null : (
        <LanguageSelectorContainer>
          <Row>
            <Col span={12}>
              <LanguageSelectorWrapper>
                <Title $bottom='6px'>{Text.SelectLanguage}</Title>
                <Select
                  virtual
                  showSearch
                  style={{ width: 200 }}
                  defaultValue=''
                  value={languageName.value}
                  optionFilterProp='children'
                  data-testid='select-language'
                  onChange={(value) => handleChange({ value, name: 'language_name' })}
                  filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                  notFoundContent={<AddNotFound />}>
                  {languagesList.map((item, index) => {
                    if (!languages.filter((e) => e.language_name === item.name).length > 0) {
                      return (
                        <Option key={index} value={item.name}>
                          {item.name}
                        </Option>
                      )
                    }
                  })}
                </Select>
              </LanguageSelectorWrapper>
            </Col>
            <Col span={12}>
              <LanguageSelectorWrapper>
                <Title $bottom='6px'>{Text.Level}</Title>
                <Select
                  defaultValue=''
                  style={{ width: 100 }}
                  disabled={levelIsDisabled}
                  onChange={(value) => handleChange({ value, name: 'level' })}
                  data-testid='select-language-level'
                  value={languageLevel.value}
                  getPopupContainer={(triggerNode) => {
                    return triggerNode.parentNode
                  }}>
                  {languageLevels.map((item, index) => (
                    <Option value={item} key={index}>
                      <Tooltip placement='right' title={languageLevelsTooltip[item]}>
                        <div style={{ maxWidth: '100%' }}>{item}</div>
                      </Tooltip>
                    </Option>
                  ))}
                </Select>
                {languageLevel.ErrorValue ? <ErrorText>{Text.Required}</ErrorText> : <EmptyDiv />}
              </LanguageSelectorWrapper>
            </Col>
          </Row>
          <Tags
            data={tagsMapper(languages, skillsEnum.languages, skillsEnum)}
            removeItemFunc={removeLanguage}
            template={skillsEnum.languages}
            skillsEnum={skillsEnum}
            categoryName={LanguagesSkills}
            threshold={threshold}
            onDragTags={onDragTags}
          />
        </LanguageSelectorContainer>
      )}
    </Modal>
  )
}

ModalLanguages.propTypes = {
  open: PropTypes.bool,
  setModal: PropTypes.func,
  initialState: PropTypes.array,
  setModelProfile: PropTypes.func,
  skillsEnum: PropTypes.object,
  isSavedChange: PropTypes.func,
  threshold: PropTypes.number
}
